import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LogoImage } from "@/components/Logo";

const inputClass =
  "w-full bg-[#141414] border border-white/15 px-5 py-4 text-sm text-[#F5F5F5] placeholder:text-[#5b5b63] focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin/submissions", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await login(email, password);
      if (data.role !== "admin") {
        setError("This account does not have administrator access.");
        return;
      }
      navigate("/admin/submissions", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="grain-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md border border-white/10 bg-[#0E0E0E] p-10 sm:p-12"
      >
        <div className="flex flex-col items-center mb-10">
          <LogoImage className="h-20 w-auto mb-6" />
          <h1 className="font-serif-display text-3xl text-[#F5F5F5]" data-testid="admin-login-heading">Administrator</h1>
        </div>
        <form onSubmit={submit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="block text-[0.65rem] tracking-[0.3em] uppercase text-white mb-3">Email</label>
            <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@sheglobal.global" className={inputClass} autoComplete="username" />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-[0.65rem] tracking-[0.3em] uppercase text-white mb-3">Password</label>
            <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••••" className={inputClass} autoComplete="current-password" />
          </div>
          {error && (
            <p className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit-btn"
            className="w-full bg-[#D4AF37] text-black text-xs tracking-[0.3em] uppercase font-semibold px-10 py-5 hover:bg-[#C5A24D] disabled:opacity-60 transition-colors duration-300"
          >
            {submitting ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <a href="/" className="block text-center mt-8 text-[0.65rem] tracking-[0.3em] uppercase text-[#5b5b63] hover:text-[#D4AF37] transition-colors duration-300" data-testid="admin-login-back-link">
          ← Back to site
        </a>
      </motion.div>
    </div>
  );
}
